import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  Button, 
  Input, 
  PasswordInput, 
  Logo, 
  DualPaneLayout, 
  SocialLogin,
  colors, 
  spacing, 
  typography 
} from '../components';
import { Ionicons } from '@expo/vector-icons';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService, syncApiServiceWithStore } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { TimezoneService } from '../services/timezone.service';
import { decodeBase64Url } from '../utils/token-utils';
import { loadCredentials, saveCredentials, clearCredentials, setTrustDeviceIntent } from '../utils/credentialManager';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { FEATURE_FLAGS } from '../config/api.config';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const LoginScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { 
    handleSignUp, 
    handleForgotPassword, 
    handleSocialLogin,
    showNotification,
    navigateTo,
    setPendingEmail,
    setIsNewUser
  } = useAppContext();

  const { setAuthState } = useAppStore();
  const { t, isRTL } = useLocale();
  const typo = useTypography();

  const [isLoading, setIsLoading] = useState(false);

  // Social login handlers
  const onGoogleLogin = () => handleSocialLogin('google');
  const onFacebookLogin = () => handleSocialLogin('facebook');
  const onAppleLogin = () => handleSocialLogin('apple');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    let isMounted = true;

    loadCredentials()
      .then((credentials) => {
        if (credentials && isMounted) {
          setRememberMe(true);
          setEmail(credentials.email || '');
          setPassword(credentials.password || '');
        }
      })
      .catch((error) => {
        console.error('[LoginScreen] Failed to load remembered credentials:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    if (!password.trim()) {
      newErrors.password = t('validation.required');
    } else if (password.length < 6) {
      newErrors.password = t('validation.passwordMin');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    try {
      logError('Starting login with email:', 'LoginScreen');
      
      const response = await apiService.login({ email, password });
      logError('Login response:', 'LoginScreen');
      
      // Check if login was successful (statusCode 1000 = success)
      if (response.statusCode === 1000) {
        if (response.data && response.user) {
          const { token, refreshToken } = response.data;
          const userData = response.user;
          
          // Debug: Log the user data structure
          console.log('🔍 [LoginScreen] Response user data:', userData);
          console.log('🔍 [LoginScreen] User name field:', userData.name);
          console.log('🔍 [LoginScreen] User emailVerified field:', userData.emailVerified);
          
          if (token && refreshToken && userData) {
            try {
              if (rememberMe) {
                await saveCredentials(email, password);
              } else {
                await clearCredentials();
              }
              // Consumed once by the next selectBusiness() call, which mints the
              // session's actual (phase-2) refresh token with the trusted/untrusted
              // sliding window — see credentialManager.consumeTrustDeviceIntent().
              await setTrustDeviceIntent(rememberMe);
            } catch (credentialError) {
              console.error('[LoginScreen] Failed to persist credentials:', credentialError);
            }

            // CRITICAL STEP 1: Clear old Zustand persisted state FIRST
            console.log('🔄 [LoginScreen] Clearing old AsyncStorage state...');
            await AsyncStorage.removeItem('sendifier-app-store');
            console.log('✅ [LoginScreen] Old state cleared');
            
            // 🔥 CRITICAL STEP 2: Use atomic setAuthState() instead of separate setTokens() and setUser()
            // This ensures tokens, user, and business are all set together atomically
            console.log('🔄 [LoginScreen] Setting auth state atomically...');
            
            await setAuthState({
              tokens: { token, refreshToken },
              user: {
                id: userData.id || userData._id,
                email: userData.email,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                username: userData.email ? userData.email.split('@')[0] : '',
                isEmailVerified: userData.emailVerified || false,
                isActive: true,
                mobile: userData.mobile || userData.phone,
                phone: userData.phone || userData.mobile,
                timezone: userData.timezone,
                timezoneOffset: userData.timezoneOffset,
                country: userData.country,
                city: userData.city,
                preferences: userData.preferences,
                createdAt: userData.createdAt || new Date().toISOString(),
                updatedAt: userData.updatedAt || new Date().toISOString()
              },
              source: 'login'
            });
            
            console.log('✅ [LoginScreen] Auth state set atomically - tokens, user, and business all set');
            
            // Update API service with the token
            apiService.setAuthToken(token);
            apiService.setRefreshToken(refreshToken);
            
            // Also sync with the centralized token manager
            syncApiServiceWithStore({ token, refreshToken });
            
            // ⚠️ ANDROID FIX: Add small delay to ensure token is fully synced before navigation
            // This prevents race condition where Header tries to fetch notifications before token is ready
            if (Platform.OS === 'android') {
              console.log('⏳ [LoginScreen] Android detected - waiting 300ms for token sync...');
              await new Promise(resolve => setTimeout(resolve, 300));
            }
            
            showNotification('success', 'Login Successful', 'Welcome back!');
            
            console.log('🔍 [LoginScreen] Checking onboarding status');
            
            // Step 1: Check email verification
            if (!userData.emailVerified) {
              console.log('❌ [LoginScreen] Email not verified, redirecting to OTP verification');
              setPendingEmail(userData.email);
              navigateTo('otp-verification');
              return;
            }
            console.log('✅ [LoginScreen] Email verified');
            
            // Step 2: Check business from token (already extracted by setTokens)
            // NO NEED to call checkUserBusinesses() here - it causes a race condition!
            // The business is already in the store from extractBusinessFromToken()
            try {
              const tokenPayload = JSON.parse(decodeBase64Url(token.split('.')[1]));
              console.log('🔍 [LoginScreen] Token payload:', tokenPayload);
              
              // Check if business context exists in token
              const businessId = tokenPayload.defaultBusinessId || tokenPayload.businessId || tokenPayload.business?.id || tokenPayload.business?._id;
              
              if (!businessId) {
                console.log('❌ [LoginScreen] No business in token, redirecting to business setup');
                navigateTo('business-setup');
                return;
              }
              console.log('✅ [LoginScreen] Business exists in token:', businessId);
              
              // Step 3: Check subscription
              const hasSubscription = tokenPayload.subscriptionId || tokenPayload.subscription?.id;
              if (!hasSubscription) {
                console.log('⚠️ [LoginScreen] No subscription in token, redirecting to subscription screen');
                navigateTo('subscription');
                return;
              }
              console.log('✅ [LoginScreen] Subscription exists in token:', tokenPayload.subscriptionId || tokenPayload.subscription?.id);
              
            } catch (tokenError) {
              console.error('❌ [LoginScreen] Error decoding token:', tokenError);
              // If token decode fails, let AppNavigator handle business/subscription validation
            }
            
            // Check and update user timezone (non-blocking)
            checkAndUpdateTimezone(userData).catch(error => {
              console.error('[LoginScreen] Timezone update failed (non-blocking):', error);
            });
            
            // All checks passed - navigate to home
            console.log('✅ [LoginScreen] All validations passed, navigating to home');
            navigateTo('home');
          } else {
            showNotification('error', 'Login Error', 'Invalid response data from server.');
          }
        } else {
          showNotification('error', 'Login Error', 'No user data received from server.');
        }
      } else if (response.statusCode === 1011) {
        // Handle unauthorized (invalid credentials)
        if (response.errors && response.errors.length > 0) {
          showNotification('error', 'Login Failed', response.errors[0].message);
        } else {
          showNotification('error', 'Login Failed', 'Invalid email or password');
        }
      } else {
        // Handle other API errors
        if (response.errors && response.errors.length > 0) {
          showNotification('error', 'Login Error', response.errors[0].message);
        } else {
          showNotification('error', 'Login Error', 'An unexpected error occurred');
        }
      }
    } catch (error: any) {
      logError(error, 'LoginScreen');
      
      // Handle error with unified error handling
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    }
  };

  // Local login handler that calls the local business logic
  const onLogin = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        await handleLogin();
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Check and update user timezone if needed
   */
  const checkAndUpdateTimezone = async (user: any) => {
    try {
      console.log('[LoginScreen] Checking user timezone...');
      
      // Get device timezone
      const deviceTZ = await TimezoneService.getDeviceTimezone();
      
      console.log('[LoginScreen] Device timezone:', deviceTZ.timezone);
      console.log('[LoginScreen] User profile timezone:', user.timezone);
      
      // Check if update needed
      const needsUpdate = !user.timezone || 
                         !TimezoneService.areTimezonesEqual(user.timezone, deviceTZ.timezone);
      
      if (!needsUpdate) {
        console.log('[LoginScreen] Timezone already up-to-date, no update needed');
        return;
      }
      
      // Update timezone on backend
      console.log('[LoginScreen] Updating user timezone to:', deviceTZ.timezone);
      const response = await apiService.updateUserTimezone({
        timezone: deviceTZ.timezone,
        timezoneOffset: deviceTZ.offset,
      });
      
      if (response.statusCode === 1000) {
        console.log('[LoginScreen] ✅ Timezone updated successfully');
      } else {
        console.log('[LoginScreen] ⚠️ Timezone update returned non-success code:', response.statusCode);
      }
    } catch (error) {
      console.error('[LoginScreen] Error updating timezone:', error);
      // Non-blocking error - don't throw
    }
  };

  return (
    <DualPaneLayout title="DISLIX" subtitle={t('auth.signIn')}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView 
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Header */}
            <View style={styles.header}>
              <Logo size="medium" variant="white" />
            </View>

            {/* Form */}
            <View style={styles.form}>
              <Input
                label={t('auth.email')}
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChangeText={setEmail}
                size="md"
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                error={errors.email}
                editable={!isLoading}
              />

              <PasswordInput
                label={t('auth.password')}
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChangeText={setPassword}
                size="md"
                error={errors.password}
                editable={!isLoading}
              />

              {/* Remember Me & Forgot Password */}
              <View style={[styles.utilityRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <TouchableOpacity
                  style={[styles.rememberMeButton, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                  onPress={() => setRememberMe((prev) => !prev)}
                  disabled={isLoading}
                  activeOpacity={0.8}
                >
                  <View style={[styles.checkbox, rememberMe && styles.checkboxChecked]}>
                    {rememberMe && <Ionicons name="checkmark" size={14} color={colors.white} />}
                  </View>
                  <Text
                    style={[
                      styles.rememberMeText,
                      { fontFamily: typo.fontFamily.medium },
                      isLoading && styles.disabledText,
                    ]}
                  >
                    {t('auth.rememberMe')}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  onPress={handleForgotPassword}
                  disabled={isLoading}
                  style={styles.forgotPasswordContainer}
                >
                  <Text style={[
                    styles.forgotPasswordText,
                    { fontFamily: typo.fontFamily.medium },
                    isLoading && styles.disabledText
                  ]}>
                    {t('auth.forgotPassword')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Error Display */}
              {/* Error text displays removed */}

              {/* Login Button */}
              <Button
                title={t('auth.signIn')}
                onPress={onLogin}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                style={styles.loginButton}
              />

              {/* Social Login — hidden */}

              {/* Sign Up Link */}
              {(Platform.OS !== 'ios' || FEATURE_FLAGS.IOS_REGISTRATION_ENABLED) && (
                <View style={[styles.signUpContainer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <Text style={[styles.signUpText, { fontFamily: typo.fontFamily.primary }]}>{t('auth.noAccount')}</Text>
                  <TouchableOpacity
                    onPress={handleSignUp}
                    disabled={isLoading}
                  >
                    <Text style={[
                      styles.signUpLink,
                      { fontFamily: typo.fontFamily.medium },
                      isLoading && styles.disabledText
                    ]}>
                      {t('auth.signUp')}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl * 2,
  },
  form: {
    paddingHorizontal: spacing.screenPadding,
  },
  loginButton: {
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  utilityRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
  },
  rememberMeButton: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  rememberMeText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  forgotPasswordContainer: {
    alignSelf: 'flex-end',
  },
  forgotPasswordText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
  },
  signUpContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  signUpText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
  },
  signUpLink: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    textAlign: 'center',
    marginTop: spacing.sm,
    fontFamily: typography.fontFamily.primary,
  },
  disabledText: {
    opacity: 0.5,
  },
});
